import { channelClient, RequestPolicies } from 'shared/channels';
import type { ChannelUnsubscribe, ContractShapeOf, ContractEvent, ContractEventOf } from 'shared/channels';
import { readCollectionEvent, runQueuedMutation } from 'shared/channels/entity';
import { DomainMessagesTypes, type Message } from 'shared/domain/messages/message';
import type { IssueAddress } from './issueAddress';
import type {
  ExportToDocumentationRequest,
  ExportToDocumentationResultData,
  ImportFromDocumentationRequest,
  ImportFromDocumentationResultData,
  RemoveLinkedDocumentationRequest,
  RemoveLinkedDocumentationResultData,
} from './linkedDocumentation/operations';
import type { CreateIssueModel, EditIssueModel, IssueModel } from './issueModel';
import type { FilteredIssues, IssueListAddress, IssuesQuery } from './issueQuery';
import { issueContract } from './issueContract';
import type { IssueEventAddress } from 'shared/domain/issueEvent/issueEventChange';
import { parseEventDescription } from 'shared/domain/issueEvent/parseEventDescription';
import type { IssueEventInDto } from 'shared/dtos/in/issue';
import type { IssueAccessChange, IssueAccessChangeResult } from './issueAccessChange';
import type { ShareableUrlTarget } from './issueShareableUrl';
import type { IncomingEmailMessageContentInDto } from 'shared/dtos/in/issue';
import type { ChangelogOutDto } from 'shared/types/changelogV2';

type IssueEvent = ContractEvent<ContractShapeOf<typeof issueContract>>;

/**
 * Issues of the selected project as served by the service worker. Changes
 * resolve once the server accepted them and reject with its error
 * (`ChannelRequestError.messageId`).
 */
export const issueClient = {
  /** One issue, archived ones included; a project opened for the first time waits for its issues' download. */
  getOneById(id: string): Promise<IssueModel> {
    return channelClient.request(issueContract, 'getOneById', { id }, RequestPolicies.bulkRead);
  },

  /** One page of the issues matching the query. */
  getFiltered(query: IssuesQuery): Promise<FilteredIssues> {
    return channelClient.request(issueContract, 'getFiltered', query, RequestPolicies.bulkRead);
  },

  /** Every issue matching the query, e.g. for a report; may take long on big projects. */
  getAllFiltered(query: Omit<IssuesQuery, 'pagination'>): Promise<FilteredIssues> {
    return channelClient.request(issueContract, 'getFiltered', query, RequestPolicies.longRunning);
  },

  getFilteredIdList(query: IssuesQuery): Promise<IssueListAddress[]> {
    return channelClient.request(issueContract, 'getFilteredIdList', query, RequestPolicies.bulkRead);
  },

  getOfInspection(inspectionId: string, timezone: string): Promise<IssueModel[]> {
    return channelClient.request(issueContract, 'getOfInspection', { inspectionId, timezone }, RequestPolicies.bulkRead);
  },

  sync(): void {
    channelClient.notify(issueContract, 'sync', undefined);
  },

  /** Resolves with the created issue. */
  create(issue: CreateIssueModel): Promise<IssueModel> {
    return runQueuedMutation(
      issueContract,
      'create',
      (uniqueId) => ({ issue, uniqueId }),
      DomainMessagesTypes.createdEntity
    );
  },

  /** Resolves with the saved issue. */
  edit(address: IssueAddress, model: EditIssueModel): Promise<IssueModel> {
    return runQueuedMutation(
      issueContract,
      'edit',
      (uniqueId) => ({ address, model, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  /** Archives the issue. */
  async remove(issueId: string): Promise<void> {
    await runQueuedMutation(
      issueContract,
      'remove',
      (uniqueId) => ({ issueId, uniqueId }),
      DomainMessagesTypes.deletedEntity
    );
  },

  /** Restores an archived issue; a refusal of the server rejects with its `messageId`. */
  async restore(issueId: string): Promise<void> {
    await runQueuedMutation(
      issueContract,
      'restore',
      (uniqueId) => ({ issueId, uniqueId }),
      DomainMessagesTypes.updatedEntity
    );
  },

  /** Adds an event; resolves with the saved event (its id binds the attachments). */
  createEvent(issueId: string, title: string, descriptionWithMentions: string): Promise<IssueEventInDto> {
    return channelClient.request(
      issueContract,
      'createEvent',
      { issueId, title, description: parseEventDescription(descriptionWithMentions) },
      RequestPolicies.mutation
    );
  },

  editEvent(address: IssueEventAddress, title: string, descriptionWithMentions: string): Promise<IssueEventInDto> {
    return channelClient.request(
      issueContract,
      'editEvent',
      { ...address, title, description: parseEventDescription(descriptionWithMentions) },
      RequestPolicies.mutation
    );
  },

  removeEvent(address: IssueEventAddress): Promise<void> {
    return channelClient.request(issueContract, 'removeEvent', address, RequestPolicies.mutation);
  },

  getIncomingEmailMessageContent(issueId: string, contentId: string): Promise<IncomingEmailMessageContentInDto> {
    return channelClient.request(
      issueContract,
      'getIncomingEmailMessageContent',
      { issueId, contentId },
      RequestPolicies.serverRead
    );
  },

  /** A link to a document of the issue (or its events, emails, linked documentation) to share. */
  getShareableUrl(target: ShareableUrlTarget): Promise<string> {
    return channelClient.request(issueContract, 'getShareableUrl', target, RequestPolicies.read);
  },

  getChangelog(issueId: string): Promise<ChangelogOutDto> {
    return channelClient.request(issueContract, 'getChangelog', { issueId }, RequestPolicies.serverRead);
  },

  changeAccess(change: IssueAccessChange): Promise<IssueAccessChangeResult> {
    return channelClient.request(issueContract, 'changeAccess', change, RequestPolicies.mutation);
  },

  /** Resolves with the created documentation once the server exported the document. */
  exportToDocumentation(request: ExportToDocumentationRequest): Promise<ExportToDocumentationResultData> {
    return channelClient.request(issueContract, 'exportToDocumentation', request, RequestPolicies.longRunning);
  },

  importFromDocumentation(request: ImportFromDocumentationRequest): Promise<ImportFromDocumentationResultData> {
    return channelClient.request(issueContract, 'importFromDocumentation', request, RequestPolicies.longRunning);
  },

  removeLinkedDocumentation(request: RemoveLinkedDocumentationRequest): Promise<RemoveLinkedDocumentationResultData> {
    return channelClient.request(issueContract, 'removeLinkedDocumentation', request, RequestPolicies.longRunning);
  },

  /** Every issue event; `switch (event.type)` narrows `event.data`. */
  onEvent(handler: (event: IssueEvent) => void): ChannelUnsubscribe {
    return channelClient.onEvent(issueContract, handler);
  },

  /** Issues saved by the server (edits, decisions, linked documentation). */
  onUpdated(
    handler: (event: ContractEventOf<typeof issueContract, DomainMessagesTypes.updatedEntity>) => void
  ): ChannelUnsubscribe {
    return channelClient.onEventOfType(issueContract, DomainMessagesTypes.updatedEntity, handler);
  },

  /** The issues when `event` is the complete collection after a download, otherwise `undefined`. */
  readCollection(event: Message): IssueModel[] | undefined {
    return readCollectionEvent<IssueModel>(DomainMessagesTypes.allIssues, event);
  },
};
